import ic_settings from '@nokia-csf-uxr/ccfk-assets/icons/settings.svg';
import ic_help from '@nokia-csf-uxr/ccfk-assets/icons/help_circle_ltr.svg';
import ic_application from '@nokia-csf-uxr/ccfk-assets/icons/application.svg';
import ic_computer from '@nokia-csf-uxr/ccfk-assets/icons/computer.svg';
import ic_internet from '@nokia-csf-uxr/ccfk-assets/icons/internet.svg';
import ic_modem from '@nokia-csf-uxr/ccfk-assets/icons/modem.svg';
import ic_density from '@nokia-csf-uxr/ccfk-assets/icons/density.svg';

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import _merge from 'lodash/merge';
import DarkTheme from '@nokia-csf-uxr/ccfk/styles/themes/dark-theme';
import { BLACK } from '@nokia-csf-uxr/freeform-design-tokens/tokens/colors';

import _throttle from 'lodash/throttle';
import ArrowTriangleDownIcon from '@nokia-csf-uxr/ccfk-assets/ArrowTriangleDownIcon';
import CalendarIcon from '@nokia-csf-uxr/ccfk-assets/CalendarIcon';
import DrillIcon from '@nokia-csf-uxr/ccfk-assets/DrillIcon';
import Tooltip from '@nokia-csf-uxr/ccfk/Tooltip';
import SelectItem, { 
  SelectListItem,
  SelectItemText,
  SelectItemButton,
  SelectItemClearButton,
  SelectItemBaseText,
} from '@nokia-csf-uxr/ccfk/SelectItem';

/**
 * Dropbox
 * It's a component to display a list of objects
 * 
 * EXAMPLE :
 * We have a list of equipments
 * [{idEquip: x1, type: 'UBT-S/T'}, {idEquip: x2, type: ....}]
*  <Dropbox 
    uniqueKey="idEquip" // Field which has a unique value
    displayInputKey="type" // Field which is used to display the value
    showStartedKey={link?.edit} // If we load a value at the initialization of the component
    startedKey={link[super.getKeyValue("equipType")]?.idEquip} // If 'showStartedKey' is true, then we define the unique key of the element for displaying
    values={(linkRef.equipNorms != null && link?.equipNorms?.index != null) ? linkRef.equipNorms[link.equipNorms.index].equipments : []} // Values which can be selected
    onChange={(newData) => this.onChange(account.token, link, newData)} // Hook when a value is selected
 */

const ONE_REM = 0.875; // 0.0625;
const getTextWidth = (text, font = '0.875rem Nokia Pure') => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;

  // adding 8px to the width to avoid possible miscalculation or pixel fractions.
  const widthPixels = Math.ceil(context.measureText(text).width);
  // convert to rem
  const width = widthPixels * ONE_REM;

  return width;
};

/**
   * Function to convert an array path : array1.array2.value
   * @param {*} array 
   * @param {*} path 
   * @returns 
   */
const getArrayPath = (array, path) => {
  if(path.includes(".")) {
    let p = path.split(".");
    let res = array[p[0]]
    for (let i = 1; i < p.length; i++) {
      const element = array[i];
      if(res[p[i]] != null) {
        res = res[p[i]];
      } else {
        return null;
      }
    }
    return res;
  }
  return array[path];
}

const ICON_CONFIGA1 = {
  src: ic_settings,
  ariaLabel: null,
};

const ICON_CONFIGA2 = {
  src: ic_help,
  ariaLabel: null,
};

const ICON_CONFIG2 = {
  src: ic_application,
  ariaLabel: null,
};

const ICON_CONFIG3 = {
  src: ic_computer,
  ariaLabel: null,
};

const ICON_CONFIG4 = {
  src: ic_internet,
  ariaLabel: null,
};

const ICON_CONFIG5 = {
  src: ic_modem,
  ariaLabel: null,
};

const ICON_CONFIGDISABLED = {
  src: ic_density,
  ariaLabel: 'disabled',
};

const ARROW_DOWN = 'ArrowDown';
const ARROW_UP = 'ArrowUp';
const ENTER_KEY = 'Enter';
const SPACE_KEY = ' ';
const ESCAPE = 'Escape';
const TAB = 'Tab';
const DELETE = 'Delete';
const BACKSPACE = 'Backspace';

export {
  ICON_CONFIGA1,
  ICON_CONFIGA2,
  ICON_CONFIG2,
  ICON_CONFIG3,
  ICON_CONFIG4,
  ICON_CONFIG5,
  ICON_CONFIGDISABLED,
  ARROW_DOWN,
  ARROW_UP,
  ENTER_KEY,
  SPACE_KEY,
  ESCAPE,
  TAB,
  DELETE,
  BACKSPACE
};

const dropDownIcons = {
  arrow: <ArrowTriangleDownIcon />,
  calendar: <CalendarIcon />,
  drill: <DrillIcon />
};

const Dropbox = (props) => {
  const {
     showIcons,
     showHeaders,
     disabled,
     required,
     variant,
     placeholder,
     error,
     errorMessage,
     iconName,
     maxWidth,
     truncateListText,
     title,
     elevationIndex,
     dark,
     darkSelectItem,
     positionModifier,
     labelId,
     autoFocus,
     onChange,
     uniqueKey,
     displayInputKey,
     showStartedKey,
     startedKey,
     values,
     dataFreeze
  } = props;

  const isSelectionKeyPressed = key => key && (key === ENTER_KEY || key === SPACE_KEY);

  const isLoaded = useRef(false);
  const clear = useRef(false);

  const selectItemRef = useRef(null);
  const selectItemButtonRef = useRef(null);
  const [value, setValue] = useState(undefined);
  const [icon, setIcon] = useState(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [selectItemWidth, setSelectItemWidth] = useState(0);
  const [listItemWidth, setListItemWidth] = useState(0);
  const [disableTooltip, setDisableTooltip] = useState(false);
  const [disableListItemTooltip, setDisableListItemTooltip] = useState(true);

  const [uniqueKeyValue, setUniqueKeyValue] = useState(undefined);
  const [selected, setSelected] = useState(undefined);

  const overflowModifiers = { offset: { offset: '0, -104' } };
  const tooltipText = value ? value : placeholder;
  const tooltipProps = {
    placement: "bottom",
    trigger: ['hover', 'focus'],
    tooltip: tooltipText ? tooltipText : '', // Tooltip requires this property value.
    fallbackPlacements: [ 'top', 'right', 'left'],
    modifiers: overflowModifiers,
  };

  let modifiedTextInputTheme = DarkTheme.TEXT_INPUT;
  modifiedTextInputTheme.OUTLINED.background = BLACK;
  modifiedTextInputTheme.UNDERLINED.background = BLACK;


  const shouldDisplayTooltip = () => {
    // calculate value length and compare to selectitem length to determine if a Tooltip is necessary
    const valueWidth = getTextWidth(tooltipText); // returns rem
    if (Math.floor(valueWidth) >= Math.floor(selectItemWidth)){
      setDisableTooltip(false);
    } else {
      setDisableTooltip(true);
    }
    const listItemTextWidth = getTextWidth('User 1 has a very long sentence for the text and it may have to truncate.'); // returns rem
    if (Math.floor(listItemTextWidth) >= Math.floor(listItemWidth)){
      setDisableListItemTooltip(false);
    } else {
      setDisableListItemTooltip(true);
    }
  }
  useEffect(() => {
    shouldDisplayTooltip();
  }, [value, placeholder, selectItemWidth]);

  const updateSelectItemWidth = _throttle(() => {
    // subtract dropdown arrow width from select width subtract
    const DROPDOWN_WIDTH_PIXELS = 34;
    const PADDING_PIXELS = 43;
    const selRef = selectItemRef && selectItemRef.current && selectItemRef.current.selectItemRef;
    if (selRef) {
      const { clientWidth: width } = selRef;
      const selectItemWidthPixels = width - DROPDOWN_WIDTH_PIXELS - PADDING_PIXELS;
      setSelectItemWidth(selectItemWidthPixels * ONE_REM);
      const LIST_ITEM_NON_TEXT_SPACE = 92;
      // const listItemWidthPixels = width - 16 - 24 - 12 - 12 - 12 - 16;
      const listItemWidthPixels = width - LIST_ITEM_NON_TEXT_SPACE;
      setListItemWidth(listItemWidthPixels * ONE_REM);
    }
  }, 50);
  useLayoutEffect(() => {
    updateSelectItemWidth();
    window.addEventListener('resize', updateSelectItemWidth);
    return () => {
      window.removeEventListener('resize', updateSelectItemWidth);
    };
  }, [showIcons, selectItemRef])

  // handle list item selection and close dropdown list after item is selected
  const handleEvent = (uniqueId, value, data) => (event) => {
    const { type } = event;
    if (type === 'keydown') {
      if (isSelectionKeyPressed(event.key)) {
        setUniqueKeyValue(uniqueId);
        setValue(value);
        setIsOpen(false);
        onChange(data);
      }
    } else if (type === 'click') {
      setUniqueKeyValue(uniqueId);
      setValue(value);
      setIsOpen(false);
      onChange(data);
    }
  };

  // Render the necessary selected values and desired dropdown icon into the SelectItem base area.
  const renderSelectItemValues = (props)  => {
 // only show ClearButton when there is a value and the dropdown is open

 const showClearButton = !disabled && (value !== undefined) && isOpen;
    return (
      <SelectItemButton
        ref={selectItemButtonRef}
        placeholder={placeholder}
        dropdownIcon={dropDownIcons[iconName]}
        inputProps={{ value: value ? value : placeholder }}
        aria-label={value ? value : placeholder}
        role="combobox"
        {...props}
      >
        {value && <SelectItemBaseText>{value}</SelectItemBaseText>}
      </SelectItemButton>
    )
  };

  function loadValues() {
    let lines = [];
    let found = false;
    for (let i = 0; i < values.length; i++) {
      const line = values[i];

      if(uniqueKeyValue == line[uniqueKey]) {
        found = true;
      }

      let label = getArrayPath(line,displayInputKey);
      lines.push(<SelectListItem
        id={"selectitem-"+label+"-"+line[uniqueKey]}
        selected={uniqueKeyValue == line[uniqueKey] }
        onClick={handleEvent(line[uniqueKey], label, line)}
        onKeyDown={handleEvent(line[uniqueKey], label, line)}
        role="option"
        >
        <SelectItemText>{label}</SelectItemText>
      </SelectListItem>)

      if(!isLoaded.current && startedKey == line[uniqueKey]) {
        setUniqueKeyValue(line[uniqueKey]);
        setValue(label);
        onChange(line);
        isLoaded.current = true;
      }
    }
    if(!found && !clear.current && !dataFreeze) {
      setUniqueKeyValue(null);
      setValue(null);
      clear.current = true;
      isLoaded.current = false;
    } else {
      clear.current = false;
    }

    return lines
  }

  return (
    <Tooltip {...tooltipProps} disable={disableTooltip}>
        <SelectItem
        aria-labelledby={labelId}
        ref={selectItemRef}
        variant={variant}
        disabled={disabled}
        error={error}
        errorMessage={errorMessage}
        isOpen={isOpen}
        onOpen={() => { setIsOpen(true); }}
        onClose={() => { setIsOpen(false); }}
        maxWidth={maxWidth}
        truncateListText={truncateListText}
        theme={darkSelectItem ? { LIST: { ...DarkTheme.LIST }, TEXT_INPUT: { ...modifiedTextInputTheme }} : undefined}
        listProps={{
          ulProps: { role: "listbox", id: "selectitem-dropdown-list" },
          elevationProps: {
            elevationIndex: elevationIndex,
            dark: dark,
            theme: darkSelectItem ? DarkTheme.ELEVATIONS : undefined,
          },
          style: {maxWidth: maxWidth ? '31.25rem': undefined }}
        }
        menuProps={{
          positioningOptions: {
            modifiers: [
            { name: 'offset', options: { offset: [0, positionModifier] } },
            ]
          },
        }}
        autoFocus={autoFocus}
        renderSelectItemBase={renderSelectItemValues}
      >
        {loadValues()}
        
      </SelectItem>
    </Tooltip>
  )
}

Dropbox.propTypes = {
  showIcons: PropTypes.bool,
  showHeaders: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  variant: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  iconName: PropTypes.any,
  maxWidth: PropTypes.bool,
  truncateListText: PropTypes.bool,
  title: PropTypes.string,
  elevationIndex: PropTypes.number,
  dark: PropTypes.bool,
  darkSelectItem: PropTypes.bool,

  values: PropTypes.array,
  displayInputKey: PropTypes.string, // Key in an object which is the displayed name
  uniqueKey: PropTypes.string,
  showStartedKey: PropTypes.bool,
  startedKey: PropTypes.number,
  onChange: PropTypes.func,

  dataFreeze: PropTypes.bool // If true then the selected data is not in the current data input, this one is being kept
};

Dropbox.defaultProps = {
   showIcons: true,
   showHeaders: true,
   disabled: false,
   required: false,
   variant: 'outlined',
   placeholder: 'Select an option',
   error: false,
   errorMessage: undefined,
   iconName: dropDownIcons.arrow,
   maxWidth: false,
   truncateListText: true,
   title: 'SelectItem Label',
   elevationIndex: 8,
   dark: false,
   darkSelectItem: false,

   values: [],
   displayInputKey: "name",
   showStartedKey: false,
   startedKey: -1,
   uniqueKey: "",
   dataFreeze: false
};

export default Dropbox;